/**
 * Trust row — one shared voice for the signal contract.
 *
 * The truth engine stamps every signal with evidence_status, confidence_pct,
 * trust_tier and a per-axis confidence_breakdown. Every surface that renders
 * a signal (bubble card, radar card, area snapshot) shows the same compact
 * line, built here, so the contract reads identically everywhere:
 *
 *     Verified · Confidence 82% · Official source
 *
 * Rules:
 *   - Render ONLY what the data carries. RSS news items have none of these
 *     fields and get null — never invent a confidence for a news article.
 *   - Plain language, no jargon: "source_reported" → "Source-reported".
 *   - The breakdown goes in a hover title, not the visible line — proof on
 *     demand, not a debug dump.
 */

/** Structural shape — any incident-like object qualifies (the loader's
 *  RawIncident, incident-cards' ScoredIncident.raw, …). Fields are the
 *  truth engine's signal contract; all optional because the RSS news feed
 *  carries none of them. */
export interface TrustStampedItem {
  trust_tier?: number | null;
  confidence_pct?: number | null;
  evidence_status?: string | null;
  confidence_breakdown?: Record<string, number> | null;
}

export function trustTierLabel(tier: number | undefined | null): string | null {
  switch (tier) {
    case 1:
      return "Official source";
    case 2:
      return "Trusted media";
    case 3:
      return "Community";
    default:
      return null;
  }
}

export function evidenceLabel(
  status: string | null | undefined
): string | null {
  switch (status) {
    case "verified":
      return "Verified";
    case "source_reported":
      return "Source-reported";
    case "inferred":
      return "Inferred";
    case "disputed":
      return "Disputed";
    default:
      return null;
  }
}

/**
 * Severity speaks in words, never bare integers (design language §8):
 * 1–2 → "Routine", 3 → "Notable", 4–5 → "Serious". Null when the data
 * carries no severity — absence is shown as absence, never defaulted.
 */
export function severityLabel(severity?: number | null): string | null {
  if (typeof severity !== "number" || !Number.isFinite(severity)) return null;
  if (severity >= 4) return "Serious";
  if (severity >= 3) return "Notable";
  if (severity >= 1) return "Routine";
  return null;
}

export type TrustBadgeTone = "ok" | "neutral" | "disputed";

export interface TrustBadge {
  /** "Verified" / "Source-reported" / "Inferred" / "Disputed". */
  label: string;
  /** Colour family the card maps to tokens; disputed also gets a shape cue. */
  tone: TrustBadgeTone;
}

/**
 * Card-head evidence badge — the hierarchy inversion: evidence status leads
 * the card, not the fine print. Same mapping as trustLineFor; null when the
 * item carries no engine contract (RSS news) so callers can say so honestly.
 */
export function trustBadgeFor(item: TrustStampedItem): TrustBadge | null {
  const label = evidenceLabel(item.evidence_status);
  if (!label) return null;
  const tone: TrustBadgeTone =
    item.evidence_status === "disputed"
      ? "disputed"
      : item.evidence_status === "verified"
        ? "ok"
        : "neutral";
  return { label, tone };
}

export interface TrustLine {
  /** "Verified · Confidence 82% · Official source" */
  text: string;
  /** Hover/long-press detail: per-axis breakdown, when provided. */
  title: string | null;
  /** True when the engine marked the signal disputed — style it so. */
  disputed: boolean;
}

export function trustLineFor(item: TrustStampedItem): TrustLine | null {
  const parts: string[] = [];
  const evidence = evidenceLabel(item.evidence_status);
  if (evidence) parts.push(evidence);
  if (typeof item.confidence_pct === "number" && Number.isFinite(item.confidence_pct)) {
    parts.push(`Confidence ${Math.round(item.confidence_pct)}%`);
  }
  const tier = trustTierLabel(item.trust_tier);
  if (tier) parts.push(tier);
  if (parts.length === 0) return null;

  let title: string | null = null;
  const bd = item.confidence_breakdown;
  if (bd && typeof bd === "object") {
    const axes = Object.entries(bd)
      .filter(([k, v]) => k !== "combined" && typeof v === "number")
      .map(([k, v]) => `${k.replace(/_/g, " ")} ${Math.round(v as number)}`);
    if (axes.length > 0) title = `Confidence axes — ${axes.join(" · ")}`;
  }

  return {
    text: parts.join(" · "),
    title,
    disputed: item.evidence_status === "disputed",
  };
}
